class UgcVideoPlayer extends HTMLElement {
  constructor() {
    super();

    this.entries = [];
    this.currentIndex = -1;
    this.isOpen = false;
    this.slideAnimating = false;
    this.activeVideoIndex = 0;
    this.muted = true;
    this.progressRafId = null;
    this.previewObserver = null;
    this.visiblePreviewVideos = new Set();
    this.activePreviewVideo = null;

    this.autoplayOnOpen = this.dataset.autoplayOnOpen !== "false";
    this.startMuted = this.dataset.startMuted !== "false";

    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handlePlayerClick = this.handlePlayerClick.bind(this);
    this.handleVideoPlay = this.handleVideoPlay.bind(this);
    this.handleVideoPause = this.handleVideoPause.bind(this);
    this.handleVideoTimeUpdate = this.handleVideoTimeUpdate.bind(this);
    this.handleVideoLoadedMetadata = this.handleVideoLoadedMetadata.bind(this);
    this.handlePreviewIntersect = this.handlePreviewIntersect.bind(this);
    this.handlePreviewLoadedData = this.handlePreviewLoadedData.bind(this);
    this.handlePreviewEnded = this.handlePreviewEnded.bind(this);
    this.tickProgress = this.tickProgress.bind(this);
  }

  connectedCallback() {
    this.player = this.querySelector("[data-ugc-player]");
    this.videos = Array.from(this.querySelectorAll("[data-video-slot]"));
    this.loader = this.querySelector("[data-loader]");
    this.progressBar = this.querySelector("[data-progress]");
    this.muteButton = this.querySelector("[data-mute]");
    this.previewVideos = Array.from(
      this.querySelectorAll("[data-ugc-preview-video]")
    );

    this.entries = Array.from(this.querySelectorAll("[data-ugc-entry]")).sort(
      (a, b) => Number(a.dataset.index) - Number(b.dataset.index)
    );

    if (
      !this.player ||
      this.videos.length < 2 ||
      this.entries.length === 0
    ) {
      return;
    }

    this.muted = this.startMuted;
    this.videos.forEach((video) => {
      video.muted = true;
      video.addEventListener("play", this.handleVideoPlay);
      video.addEventListener("pause", this.handleVideoPause);
      // Fallbacks for when rAF can't run or `duration` wasn't ready at play time.
      video.addEventListener("timeupdate", this.handleVideoTimeUpdate);
      video.addEventListener("loadedmetadata", this.handleVideoLoadedMetadata);
      // Note: deliberately no `ended` listener — videos are `loop`'d, and on
      // looping media `ended` would otherwise stop the progress rAF each cycle.
    });

    this.updateMuteButton();
    this.setupPreviewObserver();

    this.addEventListener("click", this.handleTriggerClick);
    this.player.addEventListener("click", this.handlePlayerClick);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleKeydown);

    this.stopProgressLoop();

    if (this.videos) {
      this.videos.forEach((video) => {
        video.removeEventListener("play", this.handleVideoPlay);
        video.removeEventListener("pause", this.handleVideoPause);
        video.removeEventListener("timeupdate", this.handleVideoTimeUpdate);
        video.removeEventListener(
          "loadedmetadata",
          this.handleVideoLoadedMetadata
        );
      });
    }

    this.teardownPreviewObserver();
  }

  // ─────────────────────────── Event handlers

  handleTriggerClick(event) {
    const trigger = event.target.closest("[data-ugc-trigger]");
    if (!trigger) return;

    event.preventDefault();
    this.open(Number(trigger.dataset.index));
  }

  handlePlayerClick(event) {
    if (event.target.closest("[data-close]")) {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.target.closest("[data-mute]")) {
      event.preventDefault();
      this.toggleMute();
      return;
    }

    if (event.target.closest("[data-prev]")) {
      event.preventDefault();
      this.navigate(-1);
      return;
    }

    if (event.target.closest("[data-next]")) {
      event.preventDefault();
      this.navigate(1);
    }
  }

  handleKeydown(event) {
    if (!this.isOpen) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
      event.preventDefault();
      this.navigate(-1);
      return;
    }

    if (event.key === "ArrowRight" || event.key === "ArrowDown") {
      event.preventDefault();
      this.navigate(1);
    }
  }

  handleVideoPlay(event) {
    // Only drive the progress bar from the currently-active slot. During a
    // navigation slide the incoming video's `play` fires before
    // `activeVideoIndex` swaps, so we'd otherwise read the wrong video.
    if (event && event.currentTarget !== this.videos[this.activeVideoIndex]) {
      return;
    }
    this.startProgressLoop();
  }

  handleVideoPause() {
    const active = this.videos[this.activeVideoIndex];
    // Videos loop, so `ended` is not wired up. A transient pause during the
    // slide swap is expected; if the active video is still playing, keep going.
    if (active && !active.paused) return;
    this.stopProgressLoop();
  }

  handleVideoTimeUpdate(event) {
    if (this.slideAnimating) return;
    if (event.currentTarget !== this.videos[this.activeVideoIndex]) return;
    this.updateProgress();
  }

  handleVideoLoadedMetadata(event) {
    if (event.currentTarget !== this.videos[this.activeVideoIndex]) return;
    this.updateProgress();
  }

  handlePreviewIntersect(entries) {
    entries.forEach((entry) => {
      const video = entry.target;

      if (entry.isIntersecting) {
        this.visiblePreviewVideos.add(video);
      } else {
        this.visiblePreviewVideos.delete(video);
        if (video === this.activePreviewVideo) {
          this.stopActivePreviewVideo();
        } else {
          this.pausePreviewVideo(video);
        }
      }
    });

    this.startPreviewRotation();
  }

  handlePreviewLoadedData(event) {
    event.currentTarget.dataset.previewReady = "true";
  }

  handlePreviewEnded() {
    const endedVideo = this.activePreviewVideo;
    if (endedVideo) {
      this.pausePreviewVideo(endedVideo, true);
    }

    this.activePreviewVideo = null;
    this.startPreviewRotation(endedVideo);
  }

  // ─────────────────────────── Open / Close

  open(index) {
    if (index < 0 || index >= this.entries.length) return;

    this.resetVideoPositions();
    this.isOpen = true;
    this.dataset.playerOpen = "true";
    this.stopPreviewRotation();

    requestAnimationFrame(() => {
      this.player.classList.add("is-active");
    });

    document.addEventListener("keydown", this.handleKeydown);

    this.loadIndex(index, 0);

    requestAnimationFrame(() => {
      this.player?.focus({ preventScroll: true });
    });
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    delete this.dataset.playerOpen;
    this.player.classList.remove("is-active");
    document.removeEventListener("keydown", this.handleKeydown);

    this.videos.forEach((video) => {
      video.pause();
      video.removeAttribute("src");
      video.load();
    });

    this.stopProgressLoop();
    this.resetVideoPositions();
    this.hideLoader();
    this.slideAnimating = false;
    this.resetProgress();

    this.currentIndex = -1;
    this.activeVideoIndex = 0;

    requestAnimationFrame(() => {
      this.startPreviewRotation();
    });
  }

  resetVideoPositions() {
    const classes = [
      "ugc-player__video--staged-top",
      "ugc-player__video--staged-bottom",
      "ugc-player__video--no-transition",
    ];
    this.videos.forEach((video, i) => {
      video.classList.remove(...classes);
      if (i !== 0) video.classList.add("ugc-player__video--staged-bottom");
    });
  }

  // ─────────────────────────── Navigation

  navigate(direction) {
    if (this.entries.length <= 1 || this.slideAnimating) return;

    const total = this.entries.length;
    const next = (this.currentIndex + direction + total) % total;
    this.loadIndex(next, direction);
  }

  loadIndex(index, direction = 0) {
    const entry = this.entries[index];
    if (!entry) return;

    this.currentIndex = index;

    // Reset the bar up-front so it doesn't linger at the previous entry's
    // position while the new video is loading / sliding in.
    this.resetProgress();

    const src = entry.dataset.videoSrc;
    const poster = entry.dataset.videoPoster;

    if (direction === 0) {
      const active = this.videos[this.activeVideoIndex];
      active.muted = this.muted;
      this.swapSource(active, src, poster);
      return;
    }

    this.slideAnimating = true;

    const currentIdx = this.activeVideoIndex;
    const nextIdx = 1 - currentIdx;
    const current = this.videos[currentIdx];
    const nextEl = this.videos[nextIdx];

    const nextStartClass =
      direction > 0
        ? "ugc-player__video--staged-bottom"
        : "ugc-player__video--staged-top";
    const currentEndClass =
      direction > 0
        ? "ugc-player__video--staged-top"
        : "ugc-player__video--staged-bottom";

    // Snap next video to start position without transition.
    nextEl.classList.remove(
      "ugc-player__video--staged-top",
      "ugc-player__video--staged-bottom"
    );
    nextEl.classList.add("ugc-player__video--no-transition", nextStartClass);

    // Start loading muted (prevents audio overlap during slide).
    nextEl.muted = true;
    this.swapSource(nextEl, src, poster);

    // Force reflow so the snap takes effect before re-enabling transition.
    void nextEl.offsetHeight;

    requestAnimationFrame(() => {
      nextEl.classList.remove("ugc-player__video--no-transition");
      nextEl.classList.remove(nextStartClass);
      current.classList.add(currentEndClass);
    });

    window.setTimeout(() => {
      current.pause();
      current.muted = true;

      // Apply user's mute preference to the new active video.
      nextEl.muted = this.muted;

      this.activeVideoIndex = nextIdx;
      this.slideAnimating = false;
      this.updateProgress();

      // The incoming video's `play` event may have fired while
      // `activeVideoIndex` was still the old slot, so its progress loop was
      // skipped. Kick it off now that the swap is complete.
      if (!nextEl.paused) {
        this.startProgressLoop();
      }
    }, 520);
  }

  // ─────────────────────────── Video source + playback

  swapSource(videoEl, src, poster) {
    this.showLoader();
    videoEl.pause();

    if (poster) {
      videoEl.setAttribute("poster", poster);
    } else {
      videoEl.removeAttribute("poster");
    }

    const onCanPlay = () => {
      this.hideLoader();
      if (this.autoplayOnOpen) {
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      }
    };

    const onError = () => this.hideLoader();

    videoEl.addEventListener("canplay", onCanPlay, { once: true });
    videoEl.addEventListener("error", onError, { once: true });

    if (src) {
      videoEl.src = src;
      videoEl.load();
    } else {
      this.hideLoader();
    }
  }

  // ─────────────────────────── Trigger previews

  setupPreviewObserver() {
    if (!this.previewVideos.length) return;

    this.previewVideos.forEach((video) => {
      video.muted = true;
      video.loop = false;
      video.addEventListener("loadeddata", this.handlePreviewLoadedData);
      video.addEventListener("ended", this.handlePreviewEnded);
    });

    if (!("IntersectionObserver" in window)) return;

    this.previewObserver = new IntersectionObserver(
      this.handlePreviewIntersect,
      {
        threshold: 0.25,
      }
    );

    this.previewVideos.forEach((video) => {
      this.previewObserver.observe(video);
    });
  }

  teardownPreviewObserver() {
    if (this.previewObserver) {
      this.previewObserver.disconnect();
      this.previewObserver = null;
    }

    if (this.previewVideos) {
      this.previewVideos.forEach((video) => {
        video.removeEventListener("loadeddata", this.handlePreviewLoadedData);
        video.removeEventListener("ended", this.handlePreviewEnded);
        this.pausePreviewVideo(video, true);
      });
    }

    this.visiblePreviewVideos.clear();
    this.activePreviewVideo = null;
  }

  getVisiblePreviewVideos() {
    return this.previewVideos.filter((video) =>
      this.visiblePreviewVideos.has(video)
    );
  }

  startPreviewRotation(afterVideo = null) {
    if (this.isOpen || this.activePreviewVideo) return;

    const visibleVideos = this.getVisiblePreviewVideos();
    if (!visibleVideos.length) return;

    let nextIndex = 0;
    if (afterVideo) {
      const previousIndex = visibleVideos.indexOf(afterVideo);
      if (previousIndex !== -1) {
        nextIndex = (previousIndex + 1) % visibleVideos.length;
      }
    }

    this.activePreviewVideo = visibleVideos[nextIndex];
    this.playPreviewVideo(this.activePreviewVideo);
  }

  stopPreviewRotation() {
    this.stopActivePreviewVideo();

    if (!this.previewVideos) return;

    this.previewVideos.forEach((video) => {
      this.pausePreviewVideo(video, true);
    });
  }

  stopActivePreviewVideo() {
    if (!this.activePreviewVideo) return;

    this.pausePreviewVideo(this.activePreviewVideo, true);
    this.activePreviewVideo = null;
  }

  playPreviewVideo(video) {
    if (this.isOpen) return;

    const src = video.dataset.previewSrc;
    if (!src) return;

    if (!video.getAttribute("src")) {
      video.src = src;
      video.load();
    }

    video.muted = true;
    video.loop = false;
    video.currentTime = 0;
    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise
        .then(() => {
          if (video === this.activePreviewVideo) {
            video.dataset.previewPlaying = "true";
          }
        })
        .catch(() => {
          if (video === this.activePreviewVideo) {
            this.activePreviewVideo = null;
            if (this.getVisiblePreviewVideos().length > 1) {
              this.startPreviewRotation(video);
            }
          }
          delete video.dataset.previewPlaying;
        });
    } else {
      video.dataset.previewPlaying = "true";
    }
  }

  pausePreviewVideo(video, reset = false) {
    video.pause();
    if (reset && video.readyState > 0) {
      video.currentTime = 0;
    }
    delete video.dataset.previewPlaying;
  }

  // ─────────────────────────── Progress

  startProgressLoop() {
    if (this.progressRafId !== null) return;
    this.progressRafId = requestAnimationFrame(this.tickProgress);
  }

  stopProgressLoop() {
    if (this.progressRafId !== null) {
      cancelAnimationFrame(this.progressRafId);
      this.progressRafId = null;
    }
  }

  tickProgress() {
    // Don't paint stale values while the two-video slide is in flight; the
    // incoming video's time is meaningless until `activeVideoIndex` swaps.
    if (!this.slideAnimating) {
      this.updateProgress();
    }

    const active = this.videos?.[this.activeVideoIndex];
    // Note: no `active.ended` check — videos loop, and on loop restart
    // `ended` can flash true momentarily, which would kill the loop.
    if (active && !active.paused) {
      this.progressRafId = requestAnimationFrame(this.tickProgress);
    } else {
      this.progressRafId = null;
    }
  }

  updateProgress() {
    if (!this.progressBar) return;

    const video = this.videos[this.activeVideoIndex];
    if (!video) return;

    const duration = video.duration;
    const currentTime = video.currentTime || 0;

    let percent = 0;
    if (duration && isFinite(duration) && duration > 0) {
      percent = Math.min(Math.max(currentTime / duration, 0), 1) * 100;
    }

    this.progressBar.style.width = `${percent}%`;
  }

  resetProgress() {
    if (this.progressBar) {
      this.progressBar.style.width = "0%";
    }
  }

  // ─────────────────────────── Loader

  showLoader() {
    if (this.loader) this.loader.hidden = false;
  }

  hideLoader() {
    if (this.loader) this.loader.hidden = true;
  }

  // ─────────────────────────── Mute

  toggleMute() {
    this.muted = !this.muted;

    this.videos.forEach((video, i) => {
      video.muted = i === this.activeVideoIndex ? this.muted : true;
    });

    this.updateMuteButton();
  }

  updateMuteButton() {
    if (!this.muteButton) return;

    this.muteButton.dataset.muted = this.muted ? "true" : "false";
    this.muteButton.setAttribute("aria-pressed", String(this.muted));

    const label = this.muted
      ? this.muteButton.dataset.labelUnmute ||
        this.muteButton.getAttribute("aria-label")
      : this.muteButton.dataset.labelMute ||
        this.muteButton.getAttribute("aria-label");

    if (label) {
      this.muteButton.setAttribute("aria-label", label);
    }
  }
}

if (!customElements.get("ugc-video-player")) {
  customElements.define("ugc-video-player", UgcVideoPlayer);
}
